/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-03
 */

/**
 * Version utility for tools.dance application.
 * Provides centralized version management and display formatting.
 */

// Import version from package.json
import packageJson from '../../package.json';

const packageVersion = process.env.REACT_APP_VERSION || packageJson.version;

/**
 * Get the current application version.
 * @returns {string} The current version string
 */
export const getAppVersion = (): string => packageVersion;

/**
 * Get formatted version string for display.
 * @param {boolean} includePrefix - Whether to include 'v' prefix
 * @returns {string} Formatted version string
 */
export const getFormattedVersion = (includePrefix: boolean = true): string => {
  const version = getAppVersion();
  return includePrefix ? `v${version}` : version;
};

/**
 * Get version with build information if available.
 * @returns {string} Version with optional build info
 */
export const getVersionWithBuild = (): string => {
  const version = getAppVersion();
  const buildTime = process.env.REACT_APP_BUILD_TIME;
  const gitHash = process.env.REACT_APP_GIT_HASH;

  let versionString = `v${version}`;

  // Add build info if available (mainly for development/debugging)
  if (buildTime || gitHash) {
    const buildInfo = [];
    if (gitHash) buildInfo.push(gitHash.substring(0, 7));
    if (buildTime) buildInfo.push(buildTime);

    if (buildInfo.length > 0) {
      versionString += ` (${buildInfo.join(', ')})`;
    }
  }

  return versionString;
};

/**
 * Check if this is a development version.
 * @returns {boolean} True if development version
 */
export const isDevelopmentVersion = (): boolean => {
  const version = getAppVersion();
  return version.includes('0.') || process.env.NODE_ENV === 'development';
};
